import { Component, Inject, OnDestroy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ClaimDocumentService } from 'src/app/core/services/claim_document.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { PersonalDocumentService } from 'src/app/core/services/personal_document.service';
import { UserService } from 'src/app/core/services/user.service';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { environment } from 'src/environments/environment';
import { BaseKycProviderComponent } from '../base-kyc-provider.component';
import {
  DocumentTypeEnum,
  KycCheckBody,
  ProcessingStatus,
} from '../document-kyc-upload-modal.component';
import { KycDataService } from 'src/app/modules/client/pages/guide/services/kyc-data.service';
import { ActionPollingService } from 'src/app/modules/client/pages/guide/services/action-polling.service';
import { KycFileValidationService } from '../services/kyc-file-validation.service';
import { KycMessageService } from '../services/kyc-message.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-veriff-kyc-provider',
  templateUrl: './veriff-kyc-provider-new.component.html',
  styleUrls: ['./veriff-kyc-provider.component.scss'],
})
export class VeriffKycProviderComponent
  extends BaseKycProviderComponent
  implements OnDestroy
{
  veriffSessionUrl: string = '';
  safeVeriffUrl!: SafeResourceUrl;
  isDevEnvironment: boolean = !environment.production;
  private messageListener?: (event: MessageEvent) => void;

  // Sobrescreve as opções permitidas pelo Veriff
  override options = [
    { value: DocumentTypeEnum.PASSPORT, label: 'Passport' },
    { value: DocumentTypeEnum.ID_CARD, label: 'ID Card' },
    { value: DocumentTypeEnum.RESIDENCE_PERMIT, label: 'Residence Permit' },
    { value: DocumentTypeEnum.DRIVERS_LICENSE, label: 'Drivers License' },
    { value: DocumentTypeEnum.UNKNOWN, label: 'Other Document' },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    dialog: MatDialog,
    pds: PersonalDocumentService,
    cds: ClaimDocumentService,
    ks: KycService,
    us: UserService,
    dialogRef: MatDialogRef<VeriffKycProviderComponent>,
    private sanitizer: DomSanitizer,
    kycService: KycDataService,
    validationService: KycFileValidationService,
    messageService: KycMessageService,
    translate: TranslateService,
    claimsService: ClaimsService,
    actionPollingService: ActionPollingService,
    router: Router,
    activatedRoute: ActivatedRoute
  ) {
    const serviceData = kycService.getData();
    if (serviceData != null) {
      data = serviceData;
    }
    super(
      data,
      dialog,
      pds,
      cds,
      ks,
      us,
      dialogRef,
      kycService,
      validationService,
      messageService,
      translate,
      claimsService,
      actionPollingService,
      router,
      activatedRoute
    );
    if (!data.provider) data.provider = 'veriff';
  }

  override async processUpload() {
    // Veriff não faz upload de arquivos, pula direto para criar sessão
    this.steps = 'PROCESSING';
    this.processing = ProcessingStatus.PROCESSING;

    try {
      // Criar callback URL
      let origin = window.location.origin;
      if (origin.startsWith('http:')) {
        origin = origin.replace('http:', 'https:');
        console.warn(
          '[Veriff] Origin URL is using HTTP, changed to HTTPS for security'
        );
      }

      const callbackUrl = `${origin}/iframe-validation-success?userid=${
        (await this.us.getCurrentUser())!.id
      }&status=success`;

      console.log('[Veriff] Callback URL:', callbackUrl);

      const kycCheckBody: KycCheckBody = {
        provider: 'veriff',
        documentType: this.documentType as any,
        user_id: (await this.us.getCurrentUser())!.id!,
        action_id: this.data.action_id,
        providerAdditionalInfo: {
          callback: callbackUrl,
        },
      };

      console.log('[Veriff] KYC Check Body:', kycCheckBody);

      const res = await this.ks.submitKycCheck(kycCheckBody);
      console.log('[Veriff] KYC Check Response:', res);

      if (!res) {
        throw new Error('Failed to get KYC response');
      }

      // Verifica se tem sessionUrl para iframe
      if (res.data && res.data.sessionUrl) {
        this.veriffSessionUrl = res.data.sessionUrl;
        this.safeVeriffUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.veriffSessionUrl
        );

        console.log('[Veriff] Session URL received:', this.veriffSessionUrl);

        this.result = {
          status: 'VERIFF_SESSION_STARTED',
          value: res.data.sessionUrl,
        };

        // Vai para o step de validação via iframe
        this.steps = 'IFRAME_VALIDATION';
        this.setupPostMessageListener();

        return;
      }

      // Caso não tenha sessionUrl, tenta usar polling normal
      if (!res.id) {
        throw new Error('Failed to get KYC check ID');
      }

      this.checkId = res.id;
      await this.pollKycCheckStatus(res.id);
    } catch (error: any) {
      console.error('[Veriff] Error:', error);
      this.currentError = this.messageService.parseBackendError(error);
      this.processing = ProcessingStatus.REJECTED;
      this.steps = 'ERROR';
      this.result = {
        status: 'ERROR',
        error: this.currentError,
      };
    }
  }

  override nextStep() {
    if (this.steps === 'TYPE-DOC') {
      // Veriff pula direto para processamento após escolher tipo de documento
      this.steps = 'PROCESSING';
      this.processUpload();
    } else {
      super.nextStep();
    }
  }

  private setupPostMessageListener() {
    // Remove listener anterior se existir
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }

    // Cria novo listener
    this.messageListener = (event: MessageEvent) => {
      console.log('Mensagem recebida:', event.data);

      // Aceita mensagens de qualquer origem, pois o iframe Success pode estar em outro domínio
      // No ambiente de produção, você pode querer restringir isso

      // Verifica se é uma mensagem de sucesso do Veriff
      if (event.data && event.data.type === 'veriff-success') {
        console.log('Mensagem de sucesso recebida do iframe');
        this.handleVeriffSuccess();
      } else if (event.data && event.data.type === 'veriff-error') {
        console.log('Mensagem de erro recebida do iframe');
        this.handleVeriffError();
      }
    };

    window.addEventListener('message', this.messageListener);
    console.log('Listener de mensagem configurado');
  }

  handleVeriffSuccess() {
    console.log('Processando sucesso da validação Veriff');
    this.processing = ProcessingStatus.APPROVED;
    this.steps = 'SUCCESS';
    this.result = {
      status: 'SUCCESS',
      value: {
        message: 'Veriff validation completed successfully',
        sessionUrl: this.veriffSessionUrl,
      },
    };
    // Notifica quem estiver ouvindo que a verificação foi concluída com sucesso
    this.removePostMessageListener();
  }

  handleVeriffError() {
    console.log('Processando erro da validação Veriff');
    this.processing = ProcessingStatus.REJECTED;
    this.steps = 'ERROR';
    this.result = {
      status: 'ERROR',
      value: {
        message: 'Veriff validation failed',
        sessionUrl: this.veriffSessionUrl,
      },
    };
    this.removePostMessageListener();
  }

  private removePostMessageListener() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = undefined;
    }
  }

  override ngOnDestroy() {
    // Call parent ngOnDestroy first
    super.ngOnDestroy();
    
    // Then handle Veriff-specific cleanup
    this.removePostMessageListener();
  }

  /**
   * Abre a URL de sessão do Veriff em uma nova janela como alternativa ao iframe
   */
  openVeriffInNewWindow(): void {
    if (this.veriffSessionUrl) {
      window.open(this.veriffSessionUrl, '_blank');
      // Atualizar o status para que o usuário saiba que precisará confirmar manualmente
      this.result = {
        status: 'VERIFF_SESSION_EXTERNAL',
        value: this.veriffSessionUrl,
      };
    } else {
      console.error('Nenhuma URL de sessão disponível para abrir');
    }
  }

  /**
   * Apenas para testes: Simula um fluxo de callback do Veriff sem precisar passar pelo serviço real
   * @param status Define se será simulado um sucesso ('success') ou erro ('error')
   */
  async simulateVeriffCallback(
    status: 'success' | 'error' = 'success'
  ): Promise<void> {
    console.log(`Simulando retorno do Veriff com status: ${status}`);

    // Obtém o ID do usuário atual
    let userId = 'test-user';
    try {
      const user = await this.us.getCurrentUser();
      if (user && user.id) {
        userId = user.id;
      }
    } catch (error) {
      console.warn(
        'Não foi possível obter o ID do usuário atual, usando ID de teste'
      );
    }

    // Cria URL do iframe-validation-success com parâmetros simulados
    const callbackUrl = `${window.location.origin}/iframe-validation-success?userid=${userId}&status=${status}`;
    console.log('URL de teste para simular callback:', callbackUrl);

    // Apresenta essa URL no iframe para testar o fluxo de callback
    this.safeVeriffUrl =
      this.sanitizer.bypassSecurityTrustResourceUrl(callbackUrl);

    // Configura o listener para receber a mensagem de volta
    this.setupPostMessageListener();

    // Vá para o step de validação do iframe
    this.steps = 'IFRAME_VALIDATION';
  }
}
